import {
  Fragment,
  type ComponentProps,
  type ComponentType,
  type ElementType,
  type ReactNode,
} from "react";
import { Icon as IconBase } from "@mui/material";
import { getFormatDate, getImage } from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import RpImageTextInline from "@/components/RpImageTextInline";
import { MAILING_TYPES } from "@/config/constants";
import CreativeMediaPreview from "@/features/campaigns/components/CreativeMediaPreview";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

type CreativeData = {
  attachment?: string[];
  creative_name?: string;
  creative_type?: string;
  thumbnail_image?: string;
  thumbnail?: string;
  poster?: string;
};

type SelectedLocation = {
  locationName?: ReactNode;
  schedule_date?: string;
  isMailing?: boolean;
  mailingCost?: number;
  mailingQnt?: ReactNode;
  mailingType?: string;
  postcard_address?: string;
  isBulkDrop?: boolean;
  bulkDropCost?: number;
  bulkDropQnt?: ReactNode;
  creativeData?: CreativeData[];
};

type SelectedLocationCardProps = {
  location: SelectedLocation;
};

const Icon = IconBase as ComponentType<
  ComponentProps<typeof IconBase> & {
    component?: ElementType;
    name?: string;
  }
>;

export default function SelectedLocationCard({
  location,
}: SelectedLocationCardProps) {
  const AddressListFile = () => {
    return (
      <>
        <div className="card mt-2">
          <div className="card-body">
            <div className="row d-flex address-file">
              <div className="col-lg-4 col-md-4 col-sm-6 ">
                <div className="title-val">Mailing Addresses</div>
              </div>
              <div className="col-lg-8 col-md-8 col-sm-6">
                {location.mailingType === MAILING_TYPES.T ? (
                  <a
                    href={getImage(
                      location?.postcard_address || "",
                      "ASSET_DOWNLOAD_URL"
                    )}
                    className="file-name"
                  >
                    Postcard_address_upload.xlsx
                  </a>
                ) : (
                  <div className="no-file">N/A</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const CreativeImage = () => {
    return (
      <div className="card mt-2 creative-image-card">
        <div className="card-body">
          <div className="row">
            {location.creativeData?.map((creative) => (
              <Fragment key={creative.attachment?.join("|") || "creative"}>
                {creative.attachment?.map((attachment, index) => (
                  <div
                    className={`col-6 ${
                      creative.attachment?.length === 1
                        ? "col-12 text-center"
                        : "col-6"
                    } ${
                      index === 0 && creative.attachment?.length !== 1
                        ? "d-flex justify-content-end"
                        : ""
                    }`}
                    key={index}
                  >
                    <CreativeMediaPreview
                      attachment={attachment}
                      creativeName={creative.creative_name}
                      creativeType={creative.creative_type}
                      thumbnail={
                        creative.thumbnail_image ||
                        creative.thumbnail ||
                        creative.poster
                      }
                      poster={creative.poster}
                      imageClassName="img-fluid rounded"
                      imageStyle={{ maxHeight: 200, objectFit: "contain" }}
                    />
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const mailingCost = location.mailingCost || 0;
  const bulkDropCost = location.bulkDropCost || 0;
  const totalCost = mailingCost + bulkDropCost;
  return (
    <>
      <div className="card mt-4">
        <div className="card-body">
          <div className="row selected-campaign-detail">
            <div className="col-lg-4 col-md-4 col-sm-6 mb-2">
              <RpImageTextInline
                alignCenter={false}
                svgIcon="location_mark"
                imageProps={{
                  color: "primary",
                }}
                text={
                  <>
                    <div className="title">Location</div>
                    <div className="title-val">{location?.locationName}</div>
                  </>
                }
              />
            </div>
            <div className="col-lg-8 col-md-8 col-sm-6 mb-2">
              <RpImageTextInline
                alignCenter={false}
                svgIcon="campaign_calender"
                imageProps={{
                  color: "primary",
                }}
                text={
                  <>
                    <div className="title">Scheduled Date</div>
                    <div className="title-val">
                      {getFormatDate(location?.schedule_date, "MMM Do, YYYY")}
                    </div>
                  </>
                }
              />
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-lg-4 col-md-4 col-sm-12 mb-2">
              <div className="d-flex">
                <label className="title">Mailing : </label>
                <div className="ml-2">
                  <RpImageTextInline
                    image={
                      location.isMailing && location.mailingCost
                        ? "/img/icons/checked.png"
                        : "/img/icons/cross.png"
                    }
                  />
                </div>
              </div>

              {location.isMailing && location.mailingCost ? (
                <>
                  <div className="d-flex">
                    <label className="title">Mailing Quantity : </label>
                    <div className="ml-2 title-val">{location?.mailingQnt}</div>
                  </div>
                  <div className="d-flex">
                    <label className="title">Mailing Cost : </label>
                    <div className="ml-2 title-val color-change">
                      <CurrencyFormateText
                        value={location?.mailingCost}
                        suffix=" USD"
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            <div className="col-lg-4 col-md-4 col-sm-12 mb-2">
              <div className="d-flex">
                <label className="title">Bulk Drop : </label>
                <div className="ml-2">
                  <RpImageTextInline
                    image={
                      location?.isBulkDrop
                        ? "/img/icons/checked.png"
                        : "/img/icons/cross.png"
                    }
                  />
                </div>
              </div>

              {location.isBulkDrop ? (
                <>
                  <div className="d-flex">
                    <label className="title">Bulk Quantity : </label>
                    <div className="ml-2 title-val">
                      {location?.bulkDropQnt}
                    </div>
                  </div>
                  <div className="d-flex">
                    <label className="title">Bulk Cost : </label>
                    <div className="ml-2 title-val color-change">
                      <CurrencyFormateText
                        value={location?.bulkDropCost}
                        suffix=" USD"
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            <div className="col-lg-4 col-md-4 col-sm-12 mb-2 text-lg-right text-md-right total-amount-align">
              <div className="title-val">
                <Icon
                  component={RpSvgIcon}
                  name="wallet"
                  color="primary"
                  fontSize="small"
                  className="mr-1"
                />
                Total Cost
              </div>
              <div className="title-val">
                <CurrencyFormateText value={totalCost} suffix=" USD" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {AddressListFile()}
      {CreativeImage()}
      <hr />
    </>
  );
}
